import { Teleport, Transition, defineComponent, ref, onMounted } from 'vue'

import { useAnalytics } from '#Analytics/composables/useAnalytics'
import { useConsent } from '#Analytics/composables/useConsent'
import { UButton, UIcon } from '#components'

export interface useCookieModalConfig {
  delay?: number
}

const DEFAULT_BANNER_TEXT
  = 'Мы используем файлы cookie, чтобы улучшить работу сайта и сделать его '
    + 'удобнее. «Принять все» — включая аналитические cookies; '
    + '«Только необходимые» — без них.'

/**
 * Баннер согласия на cookies (CMP, ТЗ аналитики §11.2): категории
 * «необходимые / аналитика+маркетинг», текст и ссылка на политику —
 * из cmp-конфига витрины. Показывается, только если CMP включён на канале
 * и актуального решения нет (с учётом TTL)
 */
export const useCookieModal = (config: useCookieModalConfig) => {
  const isCookieModalOpen = ref(false)

  function onAcceptAll() {
    const { acceptAll } = useConsent()
    acceptAll()
    isCookieModalOpen.value = false
  }

  function onNecessaryOnly() {
    const { acceptNecessaryOnly } = useConsent()
    acceptNecessaryOnly()
    isCookieModalOpen.value = false
  }

  const modal = defineComponent({
    setup() {
      const { store } = useAnalytics()

      return () => {
        const cmp = store.config?.cmp
        const bannerText = cmp?.bannerText || DEFAULT_BANNER_TEXT
        const policyUrl = cmp?.privacyPolicyUrl

        return (
          <Teleport to="body">
            <Transition
              enterActiveClass="transition duration-300 ease-out"
              enterFromClass="translate-y-full opacity-0"
              enterToClass="translate-y-0 opacity-100"
              leaveActiveClass="transition duration-200 ease-in"
              leaveFromClass="translate-y-0 opacity-100"
              leaveToClass="translate-y-full opacity-0"
            >
              {isCookieModalOpen.value && (
                <div
                  class="fixed inset-x-0 bottom-0 z-50"
                  role="dialog"
                  aria-labelledby="cookie-consent-title"
                  aria-describedby="cookie-consent-description"
                >
                  <div class="border-t border-neutral-200 bg-white shadow-lg">
                    <div class="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between md:gap-6">
                      <div class="flex min-w-0 flex-1 items-start gap-3">
                        <UIcon
                          name="i-lucide-cookie"
                          class="mt-0.5 size-5 shrink-0 text-neutral-500"
                        />
                        <div class="min-w-0">
                          <p
                            id="cookie-consent-title"
                            class="text-sm font-medium text-neutral-900"
                          >
                            Файлы cookie
                          </p>
                          <p
                            id="cookie-consent-description"
                            class="mt-0.5 text-sm leading-snug text-neutral-600"
                          >
                            {bannerText}
                            {policyUrl && (
                              <>
                                {' '}
                                <a
                                  href={policyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="underline underline-offset-2 hover:text-neutral-900"
                                >
                                  Политика конфиденциальности
                                </a>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div class="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:justify-end md:w-auto">
                        <UButton
                          // @ts-ignore
                          color="neutral"
                          variant="outline"
                          class="w-full justify-center sm:w-auto"
                          onClick={onNecessaryOnly}
                        >
                          Только необходимые
                        </UButton>
                        <UButton
                          // @ts-ignore
                          color="primary"
                          class="w-full justify-center sm:w-auto"
                          onClick={onAcceptAll}
                        >
                          Принять все
                        </UButton>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Transition>
          </Teleport>
        )
      }
    },
  })

  onMounted(async () => {
    const { support } = useAnalytics()
    const { restore, bannerRequired } = useConsent()

    // Конфиг CMP нужен до решения о показе (включённость баннера, TTL)
    await support.loadConfig().catch(() => undefined)
    restore()

    if (!bannerRequired()) return

    setTimeout(() => {
      isCookieModalOpen.value = true
    }, config.delay ?? 1e3)
  })

  return {
    cookieModal: modal,
  }
}
